import LocationActions from 'actions/Location'
import { useEffect, useState, useCallback, useRef } from 'react'
import { Dispatch } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { LocationAction, LocationReducer } from 'types/location'
import { useFilterClear } from 'hooks/useFilterClear'

export const useUnities = () => {
    const ITEMS_PER_PAGE = 100
    const [filterValue, setFilterValue] = useState('')
    const [allLocations, setAllLocations] = useState<any[]>([])
    const [currentPage, setCurrentPage] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [hasMoreData, setHasMoreData] = useState(true)
    const [locationOrder, setLocationOrder] = useState<any>({ field: 'name', order: 'ORDERBY' })
    
    const locationSelector: LocationReducer = useSelector(
        (state: RootReducerState) => state.locationReducers
    )
    const locationDispatch: Dispatch<LocationAction> = useDispatch()
    const debounceTimer = useRef<NodeJS.Timeout | null>(null)
    const currentRequestFilter = useRef<string>('')
    const hasInitialDataLoaded = useRef<boolean>(false)
    const isComponentMounted = useRef<boolean>(false)

    // useEffect único para carregamento inicial
    useEffect(() => {
        if (hasInitialDataLoaded.current) {
            return
        }

        const loadInitialData = async () => {
            try {
                setIsLoading(true)
                setAllLocations([]) // Resetar lista
                setCurrentPage(0)   // Resetar página
                setHasMoreData(true)
                await LocationActions.getAllLocationsPaginate(
                    locationDispatch,
                    '0',
                    `${ITEMS_PER_PAGE}`,
                    { field: 'name', order: 'ORDERBY' }, // Usar valor inicial diretamente
                    ''
                )
                hasInitialDataLoaded.current = true
            } catch (error) {
                console.error('Erro ao carregar unidades:', error)
            } finally {
                setIsLoading(false)
            }
        }

        loadInitialData()
        isComponentMounted.current = true
    }, []) // Array de dependências vazio - executa apenas uma vez na montagem

    // useEffect para filtro e ordenação (apenas após o carregamento inicial)
    useEffect(() => {
        // Só executar se o componente já foi montado e não é o carregamento inicial
        if (!isComponentMounted.current || !hasInitialDataLoaded.current) {
            return
        }
        
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        debounceTimer.current = setTimeout(() => {
            currentRequestFilter.current = filterValue
            setAllLocations([])
            setCurrentPage(0)
            setHasMoreData(true)
            
            const loadFilteredData = async () => {
                try {
                    setIsLoading(true)
                    await LocationActions.getAllLocationsPaginate(
                        locationDispatch,
                        '0',
                        `${ITEMS_PER_PAGE}`,
                        locationOrder,
                        filterValue?.normalize('NFD').replace(/\p{M}/gu, '')
                    )
                } catch (error) {
                    console.error('[useUnities] Erro ao carregar unidades filtradas:', error)
                } finally {
                    setIsLoading(false)
                }
            }

            loadFilteredData()
        }, 300)

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
        }
    }, [filterValue, locationDispatch, locationOrder])

    useEffect(() => {
        // Atualizar lista de localizações quando o reducer mudar
        const newData = locationSelector.locationsListPaginate || []
        
        // Se é a primeira página (offset=0), substitui completamente
        if (currentPage === 0) {
            setAllLocations(newData)
        } else {
            // Se não é a primeira página, adiciona aos existentes
            setAllLocations(prev => {
                const existingIds = new Set(prev.map((item) => item.sys_id))
                const uniqueNewData = newData.filter(
                    (item) => !existingIds.has(item.sys_id)
                )
                const combinedData = [...prev, ...uniqueNewData]
                return combinedData
            })
        }

        setHasMoreData(newData.length === ITEMS_PER_PAGE)
    }, [locationSelector.locationsListPaginate, currentPage])

    // Mover useFilterClear para depois dos useEffect
    useFilterClear({
        inputId: 'input-text',
        filterValue: filterValue,
        setFilterValue: setFilterValue
    })

    const loadMore = useCallback(() => {
        if (!isLoading && hasMoreData) {
            const nextPage = currentPage + 1
            setCurrentPage(nextPage)
            
            const loadMoreData = async () => {
                try {
                    setIsLoading(true)
                    await LocationActions.getAllLocationsPaginate(
                        locationDispatch,
                        `${nextPage}`,
                        `${ITEMS_PER_PAGE}`,
                        locationOrder,
                        filterValue?.normalize('NFD').replace(/\p{M}/gu, '')
                    )
                } catch (error) {
                    console.error('[useUnities] Erro ao carregar mais unidades:', error)
                    setHasMoreData(false)
                } finally {
                    setIsLoading(false)
                }
            }

            loadMoreData()
        }
    }, [currentPage, isLoading, hasMoreData, locationDispatch, locationOrder, filterValue])

    return {
        filterValue,
        setFilterValue,
        locationSelector: {
            ...locationSelector,
            locationsListPaginate: allLocations
        },
        loadMore,
        isLoading,
        hasMoreData,
        locationOrder,
        setLocationOrder
    }
}
